import { format } from 'date-fns';
import { Layout, Orientation } from '../components/handler-components/ResizeHandler';
import { JACKPOT_MAX_FONT_SIZE, JACKPOT_THRESHOLD_TO_TRIGGER_TEXT_CHANGE } from '../data-types/casino/casinoConstants';
import { TWO_DECIMALS } from '../data-types/constants';
import { DD_MM_YYYY_HH_MM } from '../data-types/dates/datesConstants';
import { PERIOD_TIMEFRAME_ID } from '../data-types/profile/profile-sections/staying-in-control/stayingInControlEnums';
import { MOODBAR_SPEED_CORRECTION_FACTORS } from '../data-types/timing/timingConstants';
import { OddsType, OddsTypeName } from '../data-types/user/userEnums';
import { DEFAULT_BORDER_RADIUS } from '../theme';
import { addDaysToDate, addMonthsToDate, addWeeksToDate } from './date';

export const calculateDateFromNowWithPeriod = (period: string) => {
  switch (parseInt(period, 10)) {
    case PERIOD_TIMEFRAME_ID.DAYS:
      return format(addDaysToDate(1), DD_MM_YYYY_HH_MM);
    case PERIOD_TIMEFRAME_ID.WEEKS:
      return format(addWeeksToDate(1), DD_MM_YYYY_HH_MM);
    case PERIOD_TIMEFRAME_ID.MONTHS:
      return format(addMonthsToDate(1), DD_MM_YYYY_HH_MM);
    default:
      return undefined;
  }
};

export const getDateString = (dateString: string, dateFormat = 'dd/MM/yyyy'): { id?: string; date?: string } => {
  const date = new Date(dateString);
  if (getNumberOfDaysBetween(date) > 6) {
    return { date: format(date, dateFormat) };
  }

  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) {
    return { id: 'common.today' };
  }

  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  if (isTomorrow) {
    return { id: 'common.tomorrow' };
  }

  const sunday = { id: 'common.sunday' };
  const monday = { id: 'common.monday' };
  const tuesday = { id: 'common.tuesday' };
  const wednesday = { id: 'common.wednesday' };
  const thursday = { id: 'common.thursday' };
  const friday = { id: 'common.friday' };
  const saturday = { id: 'common.saturday' };
  const weekdays = [sunday, monday, tuesday, wednesday, thursday, friday, saturday];

  return weekdays[date.getDay()];
};

const getNumberOfDaysBetween = (date: Date) => {
  const differenceSeconds = Math.abs(date.getTime() - new Date().getTime());
  const days = Math.round(differenceSeconds / (24 * 60 * 60 * 1000));
  return days;
};

export const getNumberOfDecimals = (value: number) => {
  if (Math.floor(value.valueOf()) === value.valueOf()) {
    return 0;
  }

  const str = value.toString();
  if (str.indexOf('.') !== -1 && str.indexOf('-') !== -1) {
    return str.split('-')[1] || 0;
  }
  if (str.indexOf('.') !== -1) {
    return str.split('.')[1].length || 0;
  }
  return str.split('-')[1] || 0;
};

export const getDecimalFormatDisplay = (odds: number) => {
  // TODO: uncomment this after BE finishes their project of adding support for 3 decimals
  // const numberOfDecimals = getNumberOfDecimals(odds);
  //
  // return numberOfDecimals >= 3 ? odds.toFixed(3) : odds.toFixed(2);

  return !isNaN(odds) ? odds.toFixed(2) : '';
};

export const getAmericanFormatDisplay = (odds: number) => {
  if (odds === 1) {
    return '-'.concat(Infinity.toString());
  }
  if (odds >= 2) {
    return '+'.concat((100 * (odds - 1)).toFixed(0));
  }
  return !isNaN(odds) ? (-100 / (odds - 1)).toFixed(0) : '';
};

export const getOddsTypeId = (oddsType: OddsTypeName) => {
  switch (oddsType) {
    case OddsTypeName.DECIMAL:
      return OddsType.DECIMAL;
    case OddsTypeName.FRACTIONAL:
      return OddsType.FRACTIONAL;
    case OddsTypeName.AMERICAN:
      return OddsType.AMERICAN;
    default:
      return OddsType.DECIMAL;
  }
};

export const getOdds = (decimalOdds: number, fractionalOdds: string, oddsTypeId?: OddsType) => {
  switch (oddsTypeId) {
    case OddsType.DECIMAL:
      return getDecimalFormatDisplay(decimalOdds);
    case OddsType.FRACTIONAL:
      return fractionalOdds;
    case OddsType.AMERICAN:
      return getAmericanFormatDisplay(decimalOdds);
    default:
      return getDecimalFormatDisplay(decimalOdds);
  }
};

export const getRandomPercentBetween = (min: number, max: number): number => {
  const randomNumber = Math.random() * (max - min) + min;
  return randomNumber / 100;
};

export const incrementNumber = (amount: number, step: number) => {
  return (Math.floor(Number(amount)) + step).toFixed(2);
};

export const decrementNumber = (amount: number, step: number) => {
  const ceilAmount = Math.ceil(amount);

  return (ceilAmount > 0 ? ceilAmount - step : 0).toFixed(2);
};

export const roundFloatNumberDecimalsDown = (value: number, numberOfDecimals = TWO_DECIMALS): number => {
  let tenMultiplier = 1;
  for (let i = 0; i < numberOfDecimals; i += 1) {
    tenMultiplier *= 10;
  }

  const roundedNumber = Math.floor(value * tenMultiplier) / tenMultiplier;
  return parseFloat(roundedNumber.toFixed(numberOfDecimals));
};

export const calculateLottieSpeed = (score: number) => {
  const { minScore, maxScore, minSpeed, maxSpeed } = MOODBAR_SPEED_CORRECTION_FACTORS;

  let normalizedScore = (score - minScore) / (maxScore - minScore);
  normalizedScore = Math.max(Math.min(normalizedScore, 1), 0);

  return minSpeed + normalizedScore * (maxSpeed - minSpeed);
};

export const calculateScreenSizeForVideoPlayer = (
  percentageOfScreenForPlayer: number,
  displayRatioForPlayer: number,
  windowHeight: number,
  windowWidth: number,
) => {
  const computedHeight = Math.sqrt((percentageOfScreenForPlayer * windowHeight * windowWidth) / displayRatioForPlayer);
  const computedWidth = displayRatioForPlayer * computedHeight;

  return { computedHeight, computedWidth };
};

export const getComputedStreamingDialogStyles = (
  height: number,
  width: number,
  layout: Layout,
  isFullScreen: boolean,
  isBetradarDialog: boolean,
  streamRatio: number,
  topBarHeight: number,
) => {
  const isMobile = layout === Layout.MOBILE;

  const percentageOfScreenFullScreen = 33 / 100;
  const percentageOfScreenMinimized = 12 / 100;

  const { computedHeight, computedWidth } = calculateScreenSizeForVideoPlayer(percentageOfScreenFullScreen, streamRatio, height, width);

  const { computedHeight: computedSmallHeight, computedWidth: computedSmallWidth } = calculateScreenSizeForVideoPlayer(
    percentageOfScreenMinimized,
    streamRatio,
    height,
    width,
  );

  const computedDialogStyles = !isMobile
    ? isBetradarDialog
      ? isFullScreen
        ? {
            borderRadius: 10,
            height: `calc(${topBarHeight}px + ${computedHeight}px)`,
            width: `${computedWidth}px`,
          }
        : {
            borderRadius: 10,
            height: `calc(${topBarHeight}px + ${computedSmallHeight}px)`,
            width: `${computedSmallWidth}px`,
          }
      : {}
    : {
        borderRadius: `${DEFAULT_BORDER_RADIUS}px ${DEFAULT_BORDER_RADIUS}px 0px 0px`,
        backgroundColor: 'black',
      };

  return computedDialogStyles;
};

export const getComputedStreamingMobileDialogContentStyles = (
  height: number,
  width: number,
  layout: Layout,
  orientation: Orientation,
  isFullScreen: boolean,
  streamRatio: number,
  topBarHeight: number,
) => {
  const isMobile = layout === Layout.MOBILE;
  const isLandscape = orientation === Orientation.LANDSCAPE;

  const isScreenRatioBigger = isLandscape ? width / (height - topBarHeight) > streamRatio : height / (width - topBarHeight) > streamRatio;

  const computedMobileRatioWidthLandscapeFullScreenPreserveHeight = streamRatio * (height - topBarHeight);
  const computedMobileRatioHeightPortraitFullScreenPreserveHeight = streamRatio * (width - topBarHeight);

  const computedMobileRatioHeightLandscapeFullScreenPreserveWidth = (1 / streamRatio) * width;
  const computedMobileRatioHeightPortraitFullScreenPreserveWidth = (1 / streamRatio) * height;

  const computedMobileLandscapeMargin = (height - topBarHeight - computedMobileRatioHeightLandscapeFullScreenPreserveWidth) / 2;
  const computedMobilePortraitMargin = (width - topBarHeight - computedMobileRatioHeightPortraitFullScreenPreserveWidth) / 2;

  const computedMobileStyles =
    isMobile && isFullScreen
      ? isScreenRatioBigger
        ? isLandscape
          ? {
              height: `calc(100vh - ${topBarHeight}px)`,
              width: `${computedMobileRatioWidthLandscapeFullScreenPreserveHeight}px`,
              margin: 'auto',
            }
          : {
              height: `calc(100vw - ${topBarHeight}px)`,
              width: `${computedMobileRatioHeightPortraitFullScreenPreserveHeight}px`,
              margin: 'auto',
            }
        : isLandscape
          ? {
              height: `${computedMobileRatioHeightLandscapeFullScreenPreserveWidth}px`,
              width: '100vw',
              marginTop: `${computedMobileLandscapeMargin}px`,
            }
          : {
              height: `${computedMobileRatioHeightPortraitFullScreenPreserveWidth}px`,
              width: '100vh',
              marginTop: `${computedMobilePortraitMargin}px`,
            }
      : {};

  return computedMobileStyles;
};

export const getComputedJackpotTextSizes = (containerWidth: number, textWidth: number, previousFontSize: number) => {
  let fontSize = previousFontSize;

  const textToContainerRatio = containerWidth / textWidth;
  const sizeDifferenceFactor = Math.abs(1 - textToContainerRatio);

  if (sizeDifferenceFactor > JACKPOT_THRESHOLD_TO_TRIGGER_TEXT_CHANGE) {
    fontSize = Math.min(textToContainerRatio * previousFontSize, JACKPOT_MAX_FONT_SIZE);
  }

  return {
    fontSize: `${fontSize + 'px'}`,
  };
};
